import { AtoN, AtoNDefinition, Pss } from '@ais-ui/api-spec/config';
import { useLocalization } from '@ais-ui/locale-management';
import {
  AtoNDefinitionWithDate,
  AtoNWithDate,
  useFetchPSSWithType,
} from '@ais-ui/api';

import React, { useEffect, useState } from 'react';
import { AccordionListItem, Separator, TruncatedText } from '@ais-ui/ui-core';

import { cn } from '@ais-ui/utils';

import {
  DisabledShowAtonButton,
  EnabledShowAtonButton,
} from './ShowAtonButton';
import { AtonItemContent } from './AtonItemContent';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { GhVirtualAton } from '../../../assets/icons/GhVirtualAton';
import {
  DisabledVirtualDropdownMenu,
  EnabledVirtualDropdownMenu,
} from './VirtualDropdownMenu';

interface VirtualAtonItemProps {
  aton: AtoNDefinitionWithDate;
  requiresMap: boolean;
}

interface AtonItemContentProps {
  aton: AtoNDefinitionWithDate;
}

const VirtualAtonItemContent = ({ aton }: AtonItemContentProps) => {
  const { messages } = useLocalization();
  const { data: allPss } = useFetchPSSWithType('aton');
  const { formatDateWithTimeZone, formatTimeWithTimeZone } =
    useDateTimeFormat();

  const [pssNames, setPssNames] = useState<string>('');
  const [chainedPssName, setChainedPssName] = useState<string>('');

  useEffect(() => {
    if (!allPss || !allPss.records) {
      return;
    }

    const newPssNames = aton.pss
      ? aton.pss
          .map(
            (pssId) =>
              (allPss.records as Pss[]).find((pss) => pss.uuid === pssId)?.name,
          )
          .filter((name) => name != null)
          .join(', ')
      : messages.atonVirtualItemNoPss;

    setPssNames(newPssNames);
  }, [allPss, aton.pss]);

  useEffect(() => {
    if (!allPss || !allPss.records) {
      return;
    }

    const chainedAtonName = allPss.records.find(
      (pss) => pss.uuid === aton.chainedaton,
    )?.name;

    setChainedPssName(
      chainedAtonName ? chainedAtonName : messages.atonVirtualItemNoChainedAton,
    );
  }, [allPss, aton.chainedaton]);

  return (
    <>
      <div className="flex px-4 space-x-2">
        <p className="text-muted-foreground">
          {messages.atonVirtualItemPssLabel}
        </p>
        <p>{pssNames}</p>
      </div>
      <div className="flex px-4 space-x-2">
        <p className="text-muted-foreground">
          {messages.atonVirtualItemChainedPssLabel}
        </p>
        <p>{chainedPssName}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeALabel}
          </p>
          <p>{aton.sizeA}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeBLabel}
          </p>
          <p>{aton.sizeB}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeCLabel}
          </p>
          <p>{aton.sizeC}</p>
        </div>
        <div className="flex px-4 space-x-2">
          <p className="text-muted-foreground">
            {messages.atonVirtualItemSizeDLabel}
          </p>
          <p>{aton.sizeD}</p>
        </div>
      </div>

      <div className="flex flex-col pb-4 px-4 space-x-2 space-y-2">
        <p className="text-muted-foreground pb-2">
          {messages.atonVirtualItemScheduleLabel}
        </p>
        {aton?.schedule && aton.schedule.length > 0 ? (
          aton.schedule.map((schedule, index) => (
            <div key={index} className="flex justify-between items-center">
              <div className="flex flex-col items-end">
                <div>{formatDateWithTimeZone(schedule.start)}</div>
                <div className="text-muted-foreground">
                  {formatTimeWithTimeZone(schedule.start)}
                </div>
              </div>
              <p className="text-muted-foreground">to</p>
              <div className="flex flex-col items-end">
                <div>{formatDateWithTimeZone(schedule.end)}</div>
                <div className="text-muted-foreground">
                  {formatTimeWithTimeZone(schedule.end)}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div>{messages.atonVirtualItemNoScheduleValue}</div>
        )}
      </div>
    </>
  );
};

export const VirtualAtonItem = ({
  aton,
  requiresMap,
}: VirtualAtonItemProps) => {
  return (
    <AccordionListItem
      key={aton.uuid}
      value={aton.uuid as string}
      className="pr-2"
      actionButtons={
        <>
          {requiresMap && <EnabledShowAtonButton aton={aton} />}
          {!requiresMap && <DisabledShowAtonButton />}
          <Separator orientation="vertical" className="h-6 bg-border" />
        </>
      }
      triggerContent={
        <>
          <GhVirtualAton className="size-4 flex-shrink-0" />
          <div className="w-full">
            <TruncatedText
              text={aton.name || ''}
              className="text-sm text-left font-medium leading-none group-hover:underline"
              maxWidth="180px"
            />
          </div>
        </>
      }
      kebabMenu={
        requiresMap ? (
          <EnabledVirtualDropdownMenu aton={aton} />
        ) : (
          <DisabledVirtualDropdownMenu aton={aton} />
        )
      }
    >
      <AtonItemContent aton={aton}>
        <VirtualAtonItemContent aton={aton} />
      </AtonItemContent>
    </AccordionListItem>
  );
};
